import React from 'react';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { BillingStatus, BillingState, parseDecimal } from '../services/BillingService';
import { personalakteStyles as ps, colors } from '../../employees/styles/personalakteStyles';

interface BillingSummaryCardProps {
  planName: string;
  billingStatus: BillingStatus;
  periodEnd: string;
  monthlyTotal: string;
  cancelAtPeriodEnd: boolean;
  trialEnd?: string | null;
  hasSubscription: boolean;
  onStripePortal: () => void;
  billingState?: BillingState;
  trialDaysRemaining?: number;
}

const STATUS_CONFIG: Record<string, { label: string; bg: string; color: string }> = {
  trialing: { label: 'Trial', bg: colors.infoLight, color: colors.info },
  active: { label: 'Aktiv', bg: colors.successLight, color: colors.success },
  past_due: { label: 'Überfällig', bg: colors.dangerLight, color: colors.danger },
  canceled: { label: 'Gekündigt', bg: colors.dangerLight, color: colors.danger },
  unpaid: { label: 'Unbezahlt', bg: colors.warningLight, color: colors.warning },
  incomplete: { label: 'Unvollständig', bg: colors.warningLight, color: colors.warning },
};

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatCurrency(amount: number): string {
  return amount.toFixed(2).replace('.', ',') + ' €';
}

export default function BillingSummaryCard({
  planName,
  billingStatus,
  periodEnd,
  monthlyTotal,
  cancelAtPeriodEnd,
  trialEnd,
  hasSubscription,
  onStripePortal,
  billingState,
  trialDaysRemaining,
}: BillingSummaryCardProps): React.JSX.Element {
  const status = STATUS_CONFIG[billingStatus] ?? STATUS_CONFIG.active;

  if (billingState === 'billing_required') {
    return (
      <div style={{ ...ps.card, marginBottom: 20, padding: '24px 28px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: colors.danger, margin: 0, marginBottom: 6 }}>
              Testphase beendet
            </h1>
            <div style={{ fontSize: 13, color: colors.textMuted }}>
              Bitte w\u00e4hle ein Paket, um weiterzuarbeiten.
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!hasSubscription) {
    return (
      <div style={{ ...ps.card, marginBottom: 20, padding: '24px 28px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: colors.navy, margin: 0, marginBottom: 6 }}>
              Kein aktives Abonnement
            </h1>
            <div style={{ fontSize: 13, color: colors.textMuted }}>
              W\u00e4hle unten ein Paket, um loszulegen.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...ps.card, marginBottom: 20, padding: '24px 28px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: colors.navy, margin: 0 }}>
              {planName}
            </h1>
            <span style={ps.badge(status.bg, status.color)}>{status.label}</span>
          </div>
          {billingState === 'trialing' && trialEnd ? (
            <div style={{ fontSize: 13, color: colors.info, fontWeight: 500 }}>
              Dein aktueller Test l\u00e4uft noch bis {formatDate(trialEnd)}
              {trialDaysRemaining != null && ` (${trialDaysRemaining} Tage)`}
            </div>
          ) : (
            <div style={{ fontSize: 13, color: colors.textMuted }}>
              {billingState === 'active' && trialEnd
                ? `Abrechnung gestartet am ${formatDate(trialEnd)}`
                : `N\u00e4chste Abrechnung: ${formatDate(periodEnd)}`}
            </div>
          )}
          {cancelAtPeriodEnd && (
            <div style={{
              marginTop: 8, padding: '6px 10px', background: colors.warningLight,
              borderRadius: 8, fontSize: 12, color: colors.warning, fontWeight: 500,
            }}>
              Abo wird zum Ende der Laufzeit gekündigt.
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: colors.navy, lineHeight: 1.2 }}>
              {formatCurrency(parseDecimal(monthlyTotal))}
            </div>
            <div style={{ fontSize: 12, color: colors.textMuted }}>/ Monat</div>
          </div>

          <div style={{ width: 1, height: 40, background: colors.border }} />

          <div style={{ textAlign: 'right' }}>
            <button
              onClick={onStripePortal}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: 12, fontWeight: 600, color: colors.magenta, padding: 0,
              }}
            >
              <FaExternalLinkAlt size={11} />
              Zahlungsmethode & Rechnungen verwalten
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
